import { FC, PropsWithChildren } from 'react'

import { LDProvider } from 'launchdarkly-react-client-sdk'
import { HelmetProvider } from 'react-helmet-async'
import { Provider as ReduxProvider } from 'react-redux'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { StyleSheetManager, ThemeProvider } from 'styled-components'

import { SetFavicon } from 'core/components'
import ErrorBoundary from 'core/components/lib/ErrorBoundary/ErrorBoundary'
import store from 'core/reducers'
import { useBootstrapStore } from 'core/stores'
import {
  primary as defaultPrimary,
  primaryTint,
  secondary as defaultSecondary,
  tertiary as defaultTertiary,
} from 'core/styles'
import { CompanyConfigBrandAssets } from 'core/types'

import shouldForwardProp from './shouldForwardProp'

const isValidColor = (color: string | null | undefined) => {
  if (!color) return false

  const style = new Option().style
  style.color = color
  return !!style.color
}

/**
 * Inner component that wraps children with ErrorBoundary.
 * Must be inside BrowserRouter to access useLocation.
 */
const AppErrorBoundary: FC<PropsWithChildren<{}>> = ({ children }) => {
  const location = useLocation()
  return (
    <ErrorBoundary ident='AppRoot' isAppRoot resetKey={location.key}>
      {children}
    </ErrorBoundary>
  )
}

const CommonProviders: FC<PropsWithChildren<{}>> = ({ children }) => {
  const companyId = useBootstrapStore((data) => data?.company?.id)
  const theme = useBootstrapStore((data) => {
    // handle previous deeply nested config setup.
    // can remove once that is fully not in use
    const { primary, secondary, tertiary, lighten } =
      ((data?.company?.brandAssets?.colors as any)?.theme as CompanyConfigBrandAssets['colors']) ??
      data?.company?.brandAssets?.colors ??
      {}

    return {
      primary: isValidColor(primary) ? primary : defaultPrimary,
      secondary: isValidColor(secondary) ? secondary : defaultSecondary,
      tertiary: isValidColor(tertiary) ? tertiary : defaultTertiary,
      lighten: isValidColor(lighten) ? lighten : primaryTint,
    }
  })

  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppErrorBoundary>
          <LDProvider
            clientSideID={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID}
            context={{ key: companyId ?? 'unknown' }}
          >
            <ReduxProvider store={store}>
              <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                <ThemeProvider theme={theme}>
                  <SetFavicon />
                  {children}
                </ThemeProvider>
              </StyleSheetManager>
            </ReduxProvider>
          </LDProvider>
        </AppErrorBoundary>
      </BrowserRouter>
    </HelmetProvider>
  )
}

export default CommonProviders
